import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { astraAPI } from '../../../services/astraApi';
import { AnimatedCurrency, AnimatedPercent } from '../../../components/ui/AnimatedNumber';
import { SkeletonMetricCard } from '../../../components/ui/Skeleton';

const PortfolioMetrics = ({ refreshTrigger = 0 }) => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Silent refresh tracking
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPortfolioData();
  }, [refreshTrigger]);

  const fetchPortfolioData = async () => {
    try {
      // Only show loading on initial load
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      // 🔧 PRODUCTION FIX (2025-10-29): Get separate INR/USDT breakdown
      const currencyBreakdown = await astraAPI.getCurrencyBreakdown(1453);

      if (currencyBreakdown) {
        const inr = currencyBreakdown.inr;
        const usdt = currencyBreakdown.usdt;
        const combined = currencyBreakdown.combined;

        setPortfolioData({
          // Combined totals
          totalBalance: combined.total_value_inr,
          totalPnL: combined.total_pnl_inr,
          pnLPercentage: ((combined.total_pnl_inr / (inr.initial + usdt.initial * combined.exchange_rate)) * 100),

          // Day P&L (use unrealized as proxy)
          dayPnL: inr.pnl + (usdt.pnl * combined.exchange_rate),
          dayPnLPercentage: ((inr.pnl + usdt.pnl * combined.exchange_rate) / combined.total_value_inr * 100),

          // Separate currency data with realized/unrealized breakdown
          inrData: {
            initial: inr.initial,
            available: inr.available,
            invested: inr.invested,
            currentValue: inr.current_value,
            pnl: inr.pnl,
            pnlPercentage: inr.pnl_percentage,
            realizedPnl: inr.realized_pnl,  // From closed trades
            unrealizedPnl: inr.unrealized_pnl,  // From open positions
            utilized: inr.utilized_percentage,
            positions: inr.position_count,
            total: inr.total,
            explanation: inr.explanation
          },
          usdtData: {
            initial: usdt.initial,
            available: usdt.available,
            invested: usdt.invested,
            currentValue: usdt.current_value,
            pnl: usdt.pnl,
            pnlPercentage: usdt.pnl_percentage,
            realizedPnl: usdt.realized_pnl,  // From closed trades
            unrealizedPnl: usdt.unrealized_pnl,  // From open positions
            utilized: usdt.utilized_percentage,
            positions: usdt.position_count,
            total: usdt.total,
            inINR: usdt.total * combined.exchange_rate,
            explanation: usdt.explanation
          },
          exchangeRate: combined.exchange_rate,

          // Legacy fields for backward compatibility
          totalInvested: inr.initial + (usdt.initial * combined.exchange_rate),
          availableCash: inr.available + (usdt.available * combined.exchange_rate)
        });
      } else {
        throw new Error('Failed to fetch currency breakdown');
      }
    } catch (err) {
      console.error('Failed to fetch portfolio data:', err);
      setError('Failed to load portfolio data');
      setPortfolioData(null);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatUSD = (amount) => {
    return `$${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value?.toFixed(2)}%`;
  };

  if (loading && isInitialLoad) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-card border border-border rounded-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Portfolio Overview</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-warning rounded-full animate-live-pulse"></div>
            <span className="text-xs text-muted-foreground">Loading...</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <SkeletonMetricCard />
          <SkeletonMetricCard />
          <SkeletonMetricCard />
          <SkeletonMetricCard />
        </div>
      </motion.div>
    );
  }

  if (error || !portfolioData) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Portfolio Overview</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-error rounded-full"></div>
            <span className="text-xs text-muted-foreground">Error</span>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="AlertTriangle" size={24} className="text-error mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{error || 'Failed to load portfolio data'}</p>
            <button
              onClick={fetchPortfolioData}
              className="mt-2 px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-lg p-6 glass-card-hover"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Portfolio Overview</h2>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-live-pulse"></div>
          <span className="text-xs text-muted-foreground">Live</span>
        </div>
      </div>
      {/* 🔧 PRODUCTION (2025-10-29): Separate INR and USDT Display */}
      <div className="space-y-6">
        {/* INR Portfolio Section - Industry Standard Layout */}
        <div className="border-l-4 border-blue-500 pl-4">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center">
            <span className="mr-2">🇮🇳</span> INR Portfolio (Indian Equities)
          </h3>

          {/* Row 1: Cash and Positions */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-muted rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1 flex items-center">
                💰 Available to Trade
                <span className="ml-1 text-xs cursor-help" title="Cash ready for immediate trading">ⓘ</span>
              </div>
              <div className="text-lg font-bold text-foreground">{formatCurrency(portfolioData?.inrData?.available)}</div>
              <div className="text-xs text-success mt-1">Can trade now</div>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1 flex items-center">
                📊 In Open Positions
                <span className="ml-1 text-xs cursor-help" title="Money locked in active trades">ⓘ</span>
              </div>
              <div className="text-lg font-bold text-foreground">{formatCurrency(portfolioData?.inrData?.invested)}</div>
              <div className="text-xs text-muted-foreground mt-1">{portfolioData?.inrData?.positions} holdings • {portfolioData?.inrData?.utilized?.toFixed(1)}% deployed</div>
            </div>
          </div>

          {/* Row 2: P&L Breakdown (Industry Standard) */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-muted rounded-lg p-2">
              <div className="text-xs text-muted-foreground mb-1">Realized P&L</div>
              <div className={`text-sm font-bold ${portfolioData?.inrData?.realizedPnl >= 0 ? 'text-success' : 'text-error'}`}>
                {formatCurrency(portfolioData?.inrData?.realizedPnl)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Closed trades</div>
            </div>
            <div className="bg-muted rounded-lg p-2">
              <div className="text-xs text-muted-foreground mb-1">Unrealized P&L</div>
              <div className={`text-sm font-bold ${portfolioData?.inrData?.unrealizedPnl >= 0 ? 'text-success' : 'text-error'}`}>
                {formatCurrency(portfolioData?.inrData?.unrealizedPnl)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Open positions</div>
            </div>
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-2">
              <div className="text-xs text-muted-foreground mb-1 font-semibold">Total P&L</div>
              <div className={`text-sm font-bold ${portfolioData?.inrData?.pnl >= 0 ? 'text-success' : 'text-error'}`}>
                {formatCurrency(portfolioData?.inrData?.pnl)}
              </div>
              <div className={`text-xs font-medium ${portfolioData?.inrData?.pnl >= 0 ? 'text-success' : 'text-error'}`}>
                {formatPercentage(portfolioData?.inrData?.pnlPercentage)}
              </div>
            </div>
          </div>
        </div>

        {/* USDT Portfolio Section - Industry Standard Layout */}
        <div className="border-l-4 border-green-500 pl-4">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center">
            <span className="mr-2">💵</span> USDT Portfolio (Crypto Assets)
          </h3>

          {/* Row 1: Cash and Positions */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-muted rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1 flex items-center">
                💰 Available to Trade
                <span className="ml-1 text-xs cursor-help" title="Cash ready for immediate trading">ⓘ</span>
              </div>
              <div className="text-lg font-bold text-foreground">{formatUSD(portfolioData?.usdtData?.available)}</div>
              <div className="text-xs text-muted-foreground mt-1">{formatCurrency(portfolioData?.usdtData?.available * portfolioData?.exchangeRate)}</div>
            </div>
            <div className="bg-muted rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1 flex items-center">
                📊 In Open Positions
                <span className="ml-1 text-xs cursor-help" title="Money locked in active trades">ⓘ</span>
              </div>
              <div className="text-lg font-bold text-foreground">{formatUSD(portfolioData?.usdtData?.invested)}</div>
              <div className="text-xs text-muted-foreground mt-1">{portfolioData?.usdtData?.positions} holdings • {portfolioData?.usdtData?.utilized?.toFixed(1)}% deployed</div>
            </div>
          </div>

          {/* Row 2: P&L Breakdown (Industry Standard) */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-muted rounded-lg p-2">
              <div className="text-xs text-muted-foreground mb-1">Realized P&L</div>
              <div className={`text-sm font-bold ${portfolioData?.usdtData?.realizedPnl >= 0 ? 'text-success' : 'text-error'}`}>
                {formatUSD(portfolioData?.usdtData?.realizedPnl)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Closed trades</div>
            </div>
            <div className="bg-muted rounded-lg p-2">
              <div className="text-xs text-muted-foreground mb-1">Unrealized P&L</div>
              <div className={`text-sm font-bold ${portfolioData?.usdtData?.unrealizedPnl >= 0 ? 'text-success' : 'text-error'}`}>
                {formatUSD(portfolioData?.usdtData?.unrealizedPnl)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Open positions</div>
            </div>
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-2">
              <div className="text-xs text-muted-foreground mb-1 font-semibold">Total P&L</div>
              <div className={`text-sm font-bold ${portfolioData?.usdtData?.pnl >= 0 ? 'text-success' : 'text-error'}`}>
                {formatUSD(portfolioData?.usdtData?.pnl)}
              </div>
              <div className={`text-xs font-medium ${portfolioData?.usdtData?.pnl >= 0 ? 'text-success' : 'text-error'}`}>
                {formatPercentage(portfolioData?.usdtData?.pnlPercentage)}
              </div>
            </div>
          </div>
        </div>

        {/* Combined Total */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Total Portfolio Value</div>
              <div className="text-2xl font-bold text-foreground">{formatCurrency(portfolioData?.totalBalance)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Total P&L</div>
              <div className={`text-2xl font-bold ${portfolioData?.totalPnL >= 0 ? 'text-success' : 'text-error'}`}>
                {formatCurrency(portfolioData?.totalPnL)}
              </div>
              <div className={`text-sm ${portfolioData?.totalPnL >= 0 ? 'text-success' : 'text-error'}`}>
                {formatPercentage(portfolioData?.pnLPercentage)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Exchange Rate</div>
              <div className="text-lg font-bold text-foreground">₹{portfolioData?.exchangeRate?.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">per USD</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end text-xs text-muted-foreground mt-4">
        <Icon name="Clock" size={12} className="mr-1" />
        <span>Last updated: {new Date()?.toLocaleTimeString('en-IN')}</span>
      </div>
    </motion.div>
  );
};

export default PortfolioMetrics;